import * as React from 'react';
import { CubesIcon, SearchIcon, TimesIcon, ExclamationCircleIcon, UploadIcon, AutomationIcon, CheckCircleIcon } from "@patternfly/react-icons";
import { getAds, getOrganizations, deleteAd } from "@app/utils/utils";
import { useEffect, useState, useRef } from "react";
import { PageSection, EmptyState, EmptyStateIcon, EmptyStateBody, EmptyStateVariant, Title } from "@patternfly/react-core";
import { GradientButton } from "@app/components/GradientButton";
import { EditAd } from "@app/Ads/EditAd";
import { AdCard } from "@app/Ads/AdCard";
import { adStateOptions, adTypeOptions } from "@app/Ads/adStateUtils";
import { useNavigate } from "react-router-dom";

const Ads: React.FunctionComponent<any> = (props) => {

    const [adState, setAdState] = useState<any[]>([]);
    const [organizations, setOrganizations] = useState<any[]>([]);
    const [triggerRefresh, setTriggerRefresh] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    // Search and filter state
    const [searchText, setSearchText] = useState('');
    const [statusFilters, setStatusFilters] = useState<string[]>([]);
    const [typeFilter, setTypeFilter] = useState<string | null>(null);
    const [showStateDropdown, setShowStateDropdown] = useState(false);
    const stateDropdownRef = useRef<HTMLDivElement>(null);

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedAd, setSelectedAd] = useState<any>(null);

    // Create ad modal state (for empty state)

    // Error modal state
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        setIsLoading(true);
        // Check if user has organizations - redirect if not
        getOrganizations().then(function(result) {
            if (result == undefined || result == null || !result.organizations || result.organizations.length == 0) {
                navigate('/organizations');
            } else {
                setOrganizations(result.organizations);
            }
        });
        // Load ads - organizationName is already included in response
        getAds().then(function(result) {
            setAdState(result || []);
            setIsLoading(false);
        });
    }, [triggerRefresh]);

    // Poll for ads every 10 seconds
    useEffect(() => {
        const pollInterval = setInterval(() => {
            getAds().then(function(result) {
                // Only update on a successful fetch. A failed/non-OK request
                // resolves to `undefined` (see parseResponse), while a genuinely
                // empty list is `[]` (truthy). Guarding here prevents a transient
                // error from blanking the list — which would unmount any open modal.
                if (result) {
                    setAdState(result);
                }
            });
        }, 10000);

        return () => clearInterval(pollInterval);
    }, []);

    // Close state dropdown when clicking outside
    useEffect(function() {
        function handleClickOutside(event: MouseEvent) {
            if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target as Node)) {
                setShowStateDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return function() {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleEditClick = (ad: any) => {
        setSelectedAd(ad);
        setEditModalOpen(true);
    };

    const handleEditClose = () => {
        setEditModalOpen(false);
        setSelectedAd(null);
    };

    const handleEditSave = () => {
        setTriggerRefresh(!triggerRefresh);
    };

    const handleCardClick = (ad: any) => {
        navigate('/ads/' + ad.adId);
    };

    const handleDeleteClick = async (ad: any) => {
        try {
            await deleteAd(ad.adId);
            // Remove the deleted ad from local state immediately
            setAdState(prevAds => prevAds.filter(a => a.adId !== ad.adId));
        } catch (error) {
            console.error('Failed to delete ad:', error);
            setErrorMessage(`Failed to delete "${ad.name}". Please try again.`);
            setShowErrorModal(true);
        }
    };

    // Ads are already enriched with organization names in useEffect

    // Filter ads based on search text, status, and type
    const filteredAds = adState.filter(function(ad) {
        var searchLower = searchText.toLowerCase();
        var matchesSearch = searchText === '' ||
            (ad.name && ad.name.toLowerCase().indexOf(searchLower) !== -1) ||
            (ad.organizationName && ad.organizationName.toLowerCase().indexOf(searchLower) !== -1);

        var matchesStatus = statusFilters.length === 0 ||
            (ad.currentState && statusFilters.indexOf(ad.currentState) !== -1);

        var matchesType = typeFilter === null ||
            (ad.adType && ad.adType === typeFilter);

        return matchesSearch && matchesStatus && matchesType;
    }).sort(function(a, b) {
        var aTime = new Date(a.updateDateTime || a.createDateTime || 0).getTime();
        var bTime = new Date(b.updateDateTime || b.createDateTime || 0).getTime();
        return bTime - aTime;
    });

    // Toggle a status filter
    const toggleStatusFilter = function(status: string) {
        setStatusFilters(function(prev) {
            if (prev.indexOf(status) !== -1) {
                return prev.filter(function(s) { return s !== status; });
            } else {
                return prev.concat([status]);
            }
        });
    };

    const styles = {
        container: {
            padding: '24px'
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px'
        },
        title: {
            fontSize: '28px',
            fontWeight: 700,
            color: '#1f2937',
            margin: 0
        },
        subtitle: {
            fontSize: '14px',
            color: '#6b7280',
            marginTop: '4px'
        },
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '24px'
        },
        emptyStateContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px'
        },
        searchContainer: {
            marginBottom: '24px'
        },
        searchRow: {
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            flexWrap: 'wrap' as const
        },
        searchInputWrapper: {
            position: 'relative' as const,
            flex: '1',
            minWidth: '250px',
            maxWidth: '400px'
        },
        searchInput: {
            width: '100%',
            padding: '12px 40px 12px 44px',
            fontSize: '14px',
            border: '2px solid #e5e7eb',
            borderRadius: '10px',
            outline: 'none',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            background: '#ffffff'
        },
        searchIcon: {
            position: 'absolute' as const,
            left: '14px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9ca3af',
            pointerEvents: 'none' as const
        },
        clearButton: {
            position: 'absolute' as const,
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#e5e7eb',
            border: 'none',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#6b7280',
            padding: 0
        },
        filterChips: {
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap' as const
        },
        filterChip: {
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 500,
            border: '2px solid #e5e7eb',
            borderRadius: '20px',
            background: '#ffffff',
            color: '#6b7280',
            cursor: 'pointer',
            transition: 'all 0.2s'
        },
        filterChipActive: {
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 600,
            border: '2px solid #667eea',
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#ffffff',
            cursor: 'pointer',
            transition: 'all 0.2s'
        },
        stateDropdownContainer: {
            position: 'relative' as const
        },
        stateDropdownButton: {
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 500,
            border: '2px solid #e5e7eb',
            borderRadius: '10px',
            background: '#ffffff',
            color: '#374151',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
        },
        stateDropdownButtonActive: {
            borderColor: '#667eea',
            background: '#f5f3ff'
        },
        stateDropdownMenu: {
            position: 'absolute' as const,
            top: '100%',
            left: 0,
            marginTop: '4px',
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
            zIndex: 100,
            minWidth: '180px',
            padding: '8px 0'
        },
        stateDropdownItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 16px',
            fontSize: '13px',
            color: '#374151',
            cursor: 'pointer',
            transition: 'background 0.15s'
        },
        stateCheckbox: {
            width: '16px',
            height: '16px',
            borderRadius: '4px',
            border: '2px solid #d1d5db',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
        },
        stateCheckboxChecked: {
            background: '#667eea',
            borderColor: '#667eea',
            color: '#ffffff'
        },
        resultsInfo: {
            fontSize: '13px',
            color: '#6b7280',
            marginTop: '12px'
        },
        // Error modal styles
        errorOverlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
        },
        errorModal: {
            background: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        },
        errorIcon: {
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            color: '#dc2626'
        },
        errorTitle: {
            fontSize: '18px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 8px 0',
            textAlign: 'center' as const
        },
        errorText: {
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 20px 0',
            lineHeight: 1.5,
            textAlign: 'center' as const
        },
        errorButton: {
            width: '100%',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#ffffff',
            cursor: 'pointer'
        }
    };

    const emptyStateStyles = {
        wrapper: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            padding: '40px 20px'
        },
        header: {
            textAlign: 'center' as const,
            marginBottom: '40px'
        },
        mainTitle: {
            fontSize: '32px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 12px 0'
        },
        mainSubtitle: {
            fontSize: '16px',
            color: '#6b7280',
            margin: 0,
            maxWidth: '500px'
        },
        cardsContainer: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            width: '100%',
            maxWidth: '700px'
        },
        card: {
            background: '#ffffff',
            borderRadius: '16px',
            padding: '32px',
            border: '2px solid #e5e7eb',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            textAlign: 'center' as const
        },
        cardHover: {
            borderColor: '#667eea',
            boxShadow: '0 12px 40px rgba(102, 126, 234, 0.15)',
            transform: 'translateY(-4px)'
        },
        iconContainer: {
            width: '72px',
            height: '72px',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px',
            fontSize: '32px'
        },
        uploadIconBg: {
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#ffffff'
        },
        aiIconBg: {
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            color: '#ffffff'
        },
        cardLabel: {
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'uppercase' as const,
            letterSpacing: '1px',
            marginBottom: '8px'
        },
        uploadLabel: {
            color: '#3b82f6'
        },
        aiLabel: {
            color: '#8b5cf6'
        },
        cardTitle: {
            fontSize: '20px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 12px 0'
        },
        cardDescription: {
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 20px 0',
            lineHeight: 1.6
        },
        featureList: {
            listStyle: 'none',
            padding: 0,
            margin: '0 0 24px 0',
            width: '100%'
        },
        featureItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '13px',
            color: '#374151',
            padding: '6px 0',
            justifyContent: 'flex-start',
            textAlign: 'left' as const
        },
        featureIcon: {
            color: '#10b981',
            flexShrink: 0
        },
        cardButton: {
            padding: '12px 28px',
            fontSize: '14px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginTop: 'auto'
        },
        uploadButton: {
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#ffffff'
        },
        aiButton: {
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            color: '#ffffff'
        }
    };

    const [hoveredCard, setHoveredCard] = useState<string | null>(null);

    const renderEmptyState = () => (
        <div style={emptyStateStyles.wrapper}>
            {/* Header */}
            <div style={emptyStateStyles.header}>
                <h1 style={emptyStateStyles.mainTitle}>Create Your First Ad</h1>
                <p style={emptyStateStyles.mainSubtitle}>
                    Engage your audience with stunning visuals. Upload your own media or let AI generate something amazing.
                </p>
            </div>

            {/* Two Option Cards */}
            <div style={emptyStateStyles.cardsContainer}>
                {/* Upload Your Own Card */}
                <div
                    style={{
                        ...emptyStateStyles.card,
                        ...(hoveredCard === 'upload' ? emptyStateStyles.cardHover : {})
                    }}
                    onMouseEnter={function() { setHoveredCard('upload'); }}
                    onMouseLeave={function() { setHoveredCard(null); }}
                >
                    <div style={{ ...emptyStateStyles.iconContainer, ...emptyStateStyles.uploadIconBg }}>
                        <UploadIcon />
                    </div>
                    <span style={{ ...emptyStateStyles.cardLabel, ...emptyStateStyles.uploadLabel }}>
                        Option 1
                    </span>
                    <h3 style={emptyStateStyles.cardTitle}>Upload Your Own</h3>
                    <p style={emptyStateStyles.cardDescription}>
                        Have your own creative assets? Upload images or videos directly to use in your campaigns.
                    </p>
                    <ul style={emptyStateStyles.featureList}>
                        <li style={emptyStateStyles.featureItem}>
                            <CheckCircleIcon style={emptyStateStyles.featureIcon} />
                            <span>JPG, PNG, GIF, MP4 supported</span>
                        </li>
                        <li style={emptyStateStyles.featureItem}>
                            <CheckCircleIcon style={emptyStateStyles.featureIcon} />
                            <span>Up to 50MB file size</span>
                        </li>
                        <li style={emptyStateStyles.featureItem}>
                            <CheckCircleIcon style={emptyStateStyles.featureIcon} />
                            <span>Instant preview</span>
                        </li>
                    </ul>
                    <button
                        style={{ ...emptyStateStyles.cardButton, ...emptyStateStyles.uploadButton }}
                        onClick={function() { navigate('/ads/new?type=upload'); }}
                    >
                        Upload Media
                    </button>
                </div>

                {/* AI Generated Card */}
                <div
                    style={{
                        ...emptyStateStyles.card,
                        ...(hoveredCard === 'ai' ? emptyStateStyles.cardHover : {})
                    }}
                    onMouseEnter={function() { setHoveredCard('ai'); }}
                    onMouseLeave={function() { setHoveredCard(null); }}
                >
                    <div style={{ ...emptyStateStyles.iconContainer, ...emptyStateStyles.aiIconBg }}>
                        <AutomationIcon />
                    </div>
                    <span style={{ ...emptyStateStyles.cardLabel, ...emptyStateStyles.aiLabel }}>
                        Option 2
                    </span>
                    <h3 style={emptyStateStyles.cardTitle}>AI-Generated</h3>
                    <p style={emptyStateStyles.cardDescription}>
                        Let us create eye-catching custom visuals that capture attention and drive results.
                    </p>
                    <ul style={emptyStateStyles.featureList}>
                        <li style={emptyStateStyles.featureItem}>
                            <CheckCircleIcon style={emptyStateStyles.featureIcon} />
                            <span>Personalized for your brand</span>
                        </li>
                        <li style={emptyStateStyles.featureItem}>
                            <CheckCircleIcon style={emptyStateStyles.featureIcon} />
                            <span>Professional quality</span>
                        </li>
                        <li style={emptyStateStyles.featureItem}>
                            <CheckCircleIcon style={emptyStateStyles.featureIcon} />
                            <span>Ready in seconds</span>
                        </li>
                    </ul>
                    <button
                        style={{ ...emptyStateStyles.cardButton, ...emptyStateStyles.aiButton }}
                        onClick={function() { navigate('/ads/new?type=ai'); }}
                    >
                        Generate with AI
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <PageSection style={{ minHeight: '100%', background: '#f8fafc' }}>
            <div style={styles.container}>
                {/* Header - only show when there are ads */}
                {adState.length > 0 && (
                    <div style={styles.header}>
                        <div>
                            <h1 style={styles.title}>Ads</h1>
                            <p style={styles.subtitle}>
                                {`${adState.length} ad${adState.length !== 1 ? 's' : ''} in your library`}
                            </p>
                        </div>
                        <GradientButton onClick={function() { navigate('/ads/new'); }}>
                            Create a New Ad
                        </GradientButton>
                    </div>
                )}

                {/* Create Ad now lives at its own full-page route (/ads/new);
                    the header button and empty-state cards navigate there. */}

                {/* Search and Filter Bar */}
                {adState.length > 0 && (
                    <div style={styles.searchContainer}>
                        <div style={styles.searchRow}>
                            {/* Search Input */}
                            <div style={styles.searchInputWrapper}>
                                <span style={styles.searchIcon}>
                                    <SearchIcon />
                                </span>
                                <input
                                    type="text"
                                    placeholder="Search by ad name or organization..."
                                    value={searchText}
                                    onChange={function(e) { setSearchText(e.target.value); }}
                                    style={styles.searchInput}
                                />
                                {searchText && (
                                    <button
                                        style={styles.clearButton}
                                        onClick={function() { setSearchText(''); }}
                                        type="button"
                                    >
                                        <TimesIcon style={{ width: '12px', height: '12px' }} />
                                    </button>
                                )}
                            </div>

                            {/* State Filter Dropdown */}
                            <div style={styles.stateDropdownContainer} ref={stateDropdownRef}>
                                <button
                                    style={{
                                        ...styles.stateDropdownButton,
                                        ...(statusFilters.length > 0 || showStateDropdown ? styles.stateDropdownButtonActive : {})
                                    }}
                                    onClick={function() { setShowStateDropdown(!showStateDropdown); }}
                                    type="button"
                                >
                                    <span>State{statusFilters.length > 0 ? ' (' + statusFilters.length + ')' : ''}</span>
                                    <span style={{ fontSize: '10px' }}>{showStateDropdown ? '\u25B2' : '\u25BC'}</span>
                                </button>
                                {showStateDropdown && (
                                    <div style={styles.stateDropdownMenu}>
                                        {adStateOptions.map(function(option) {
                                            var isChecked = statusFilters.indexOf(option.value) !== -1;
                                            return (
                                                <div
                                                    key={option.value}
                                                    style={styles.stateDropdownItem}
                                                    onClick={function() { toggleStatusFilter(option.value); }}
                                                    onMouseEnter={function(e) { (e.currentTarget as HTMLElement).style.background = '#f3f4f6'; }}
                                                    onMouseLeave={function(e) { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                                >
                                                    <div style={{
                                                        ...styles.stateCheckbox,
                                                        ...(isChecked ? styles.stateCheckboxChecked : {})
                                                    }}>
                                                        {isChecked && <span style={{ fontSize: '10px' }}>{'\u2713'}</span>}
                                                    </div>
                                                    <span>{option.label}</span>
                                                </div>
                                            );
                                        })}
                                        {statusFilters.length > 0 && (
                                            <div
                                                style={{...styles.stateDropdownItem, borderTop: '1px solid #e5e7eb', marginTop: '4px', paddingTop: '12px', color: '#667eea', fontWeight: 500}}
                                                onClick={function() { setStatusFilters([]); }}
                                                onMouseEnter={function(e) { (e.currentTarget as HTMLElement).style.background = '#f3f4f6'; }}
                                                onMouseLeave={function(e) { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                            >
                                                Clear all
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Type Filter Chips */}
                            <div style={styles.filterChips}>
                                <button
                                    style={typeFilter === null ? styles.filterChipActive : styles.filterChip}
                                    onClick={function() { setTypeFilter(null); }}
                                    type="button"
                                >
                                    All Types
                                </button>
                                {adTypeOptions.map(function(option) {
                                    return (
                                        <button
                                            key={option.value}
                                            style={typeFilter === option.value ? styles.filterChipActive : styles.filterChip}
                                            onClick={function() { setTypeFilter(typeFilter === option.value ? null : option.value); }}
                                            type="button"
                                        >
                                            {option.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Results info */}
                        {(searchText || statusFilters.length > 0 || typeFilter) && (
                            <p style={styles.resultsInfo}>
                                Showing {filteredAds.length} of {adState.length} ad{adState.length !== 1 ? 's' : ''}
                                {searchText && <span> matching "{searchText}"</span>}
                                {statusFilters.length > 0 && <span> with state{statusFilters.length > 1 ? 's' : ''} "{statusFilters.map(function(s) { var opt = adStateOptions.find(function(o) { return o.value === s; }); return opt ? opt.label : s; }).join(', ')}"</span>}
                                {typeFilter && <span> of type "{(adTypeOptions.find(function(o) { return o.value === typeFilter; }) || { label: typeFilter }).label}"</span>}
                            </p>
                        )}
                    </div>
                )}

                {/* Ad Cards Grid */}
                {isLoading ? (
                    <div style={styles.emptyStateContainer}>
                        <p style={{ color: '#6b7280' }}>Loading ads...</p>
                    </div>
                ) : adState.length === 0 ? (
                    renderEmptyState()
                ) : filteredAds.length === 0 ? (
                    <div style={styles.emptyStateContainer}>
                        <EmptyState variant={EmptyStateVariant.large}>
                            <EmptyStateIcon icon={SearchIcon} />
                            <Title headingLevel="h4" size="lg">
                                No Results Found
                            </Title>
                            <EmptyStateBody>
                                No ads match your search criteria. Try adjusting your filters.
                            </EmptyStateBody>
                        </EmptyState>
                    </div>
                ) : (
                    <div style={styles.grid}>
                        {filteredAds.map(ad => (
                            <AdCard
                                key={ad.adId}
                                ad={ad}
                                onEditClick={handleEditClick}
                                onCardClick={handleCardClick}
                                onDeleteClick={handleDeleteClick}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Edit Ad Modal */}
            <EditAd
                ad={selectedAd}
                isOpen={editModalOpen}
                onClose={handleEditClose}
                onSave={handleEditSave}
            />

            {/* Error Modal */}
            {showErrorModal && (
                <div style={styles.errorOverlay} onClick={() => setShowErrorModal(false)}>
                    <div style={styles.errorModal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.errorIcon}>
                            <ExclamationCircleIcon style={{ fontSize: '24px' }} />
                        </div>
                        <h3 style={styles.errorTitle}>Error</h3>
                        <p style={styles.errorText}>{errorMessage}</p>
                        <button
                            style={styles.errorButton}
                            onClick={() => setShowErrorModal(false)}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}
        </PageSection>
    );
}

export { Ads };
